import {createLogger} from "@gongt/ts-stl-library/log/debug";
import {LOG_LEVEL} from "@gongt/ts-stl-library/log/levels";
import {RequestError} from "@gongt/ts-stl-library/request/request-error";
import {ErrorRequestHandler, Request, RequestHandler, Response} from "express-serve-static-core";
import {IUserProfileBase} from "../bridge/define";
import {UserPermissionClass} from "../bridge/define.permission";
import {ucExtendRequest} from "../includes/dts";
import {UserCenter} from "../includes/user-center";
import {UserError} from "../includes/user-error";
import {UserInstance} from "../includes/user-instance";

const fatal = createLogger(LOG_LEVEL.EMERG, 'error in error handler');

export class ExpressSupport<IProfile extends IUserProfileBase> {
	constructor(private uc: UserCenter<IProfile>) {
	}
	
	login(required = true): RequestHandler {
		const that = this;
		return function UserCenterUserLoginChecker(req: ucExtendRequest<IProfile>&Request, res, next) {
			let p: Promise<UserInstance<IProfile>>;
			try {
				p = that.uc.user.login(req);
			} catch (e) {
				return next(e);
			}
			p.then((data) => {
				if (req.user && req.user.token === data.token) {
					data.updatePermissionObject(req.user.permissions);
				}
				req.user = data;
				
				next();
			}, required? (e) => {
				if (e.hasOwnProperty('status')) {
					next(RequestError.standardError(e.status, e.message));
				} else {
					next(e);
				}
			} : () => {
				req.user = that.uc.user.createEmpty();
				next();
			});
		};
	}
	
	permission(permObj: UserPermissionClass, required?: boolean): RequestHandler;
	permission(permObj: UserPermissionClass, saveAs?: string, required?: boolean): RequestHandler;
	permission(permObj: UserPermissionClass, saveAs?: string|boolean, required: boolean = true): RequestHandler {
		if (typeof saveAs === 'boolean') {
			required = saveAs;
			saveAs = permObj.className;
		} else {
			if (!saveAs) {
				saveAs = permObj.className;
			}
		}
		
		const that = this;
		return function UserCenterPermissionChecker(req: ucExtendRequest&Request, res, next) {
			if (!req.user) {
				req.user = that.uc.empty();
			}
			if (req.user.permissions.hasOwnProperty(<string>saveAs)) {
				return next();
			}
			if (!req.user.hasLoggedIn) {
				req.user.permissions[<string>saveAs] = false;
				if (required) {
					return next(new UserError('require permission, but not login.'));
				}
			}
			const data = permObj.toJSON();
			data.scope = getScopeFromRequest(req, data.scope);
			req.user.permissions.check(data, <string>saveAs).then((have) => {
				if (required && !have) {
					return next(new UserError('no required permission: ' + saveAs));
				} else {
					next();
				}
			}, (e) => {
				if (e.hasOwnProperty('status')) {
					return next(RequestError.standardError(e.status, e.message));
				} else {
					return next(e);
				}
			});
		};
	}
	
	handleError(cb: (e: UserError, req: Request, res: Response) => any): ErrorRequestHandler {
		return function UserCenterDefaultErrorHandler(e: UserError, req, res, next) {
			if (e instanceof UserError) {
				try {
					const ret = cb(e, req, res);
					if (!res.headersSent && ret === undefined) {
						next(e);
					}
				} catch (e) {
					fatal(e);
					next(e);
				}
			} else {
				next(e);
			}
		};
	}
}

function getScopeFromRequest(request: Request, varName: string) {
	if (varName[0] === ':') {
		varName = varName.substr(1);
		if (request.params && request.params.hasOwnProperty(varName)) {
			return request.params[varName];
		} else if (request.query && request.query.hasOwnProperty(varName)) {
			return request.query[varName];
		} else {
			console.warn('no scope var %s', varName);
		}
	} else {
		return varName;
	}
}
